import {z} from 'zod';

import {DeepPartial} from 'react-hook-form';

import {nth} from 'ramda';

import {
  PaymentTypeSchema,
  RoundingDefinitionResponseBody,
  ValidityRequestBody,
} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {parseDate} from 'shared/web';

export type DocumentRoundingDefinitionDefaultValue = DeepPartial<RoundingDefinitionResponseBody>;

export type DocumentRoundingDefinitionFormValues = z.infer<
  ReturnType<typeof documentRoundingDefinitionFormValidations>
>;

const documentRoundingDefinitionValidityValidations = (
  defaultValue?: DeepPartial<DocumentRoundingDefinitionDefaultValue>
) =>
  z
    .array(
      z.object({
        validFrom: z.string(),
        roundType: z.string() as z.ZodType<ValidityRequestBody['roundType']>,
        roundStep: z.string() as z.ZodType<ValidityRequestBody['roundStep']>,
        taxRounding: z.boolean(),
      })
    )
    .superRefine((validations, ctx) => {
      const validationsAsDateString = validations.map((v) => parseDate(v.validFrom).toDateString());
      const nowDate = parseDate(new Date().toDateString()).getTime();

      validations.forEach((value, index) => {
        const validityFromDateDateString = nth(index, validationsAsDateString) as string;

        // Check if there is duplicate date in validations
        const duplicateDate = validationsAsDateString.filter(
          (date) => date === validityFromDateDateString
        );
        if (duplicateDate.length > 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('entity.accounting.validations.validFromUnique'),
            path: [index, 'validFrom'],
          });
        }

        const validFromDate = parseDate(validityFromDateDateString).getTime();

        // Check if date already exists in values (values are passed only while editing)
        const existingItem = defaultValue?.validities?.find(
          (item) => item?.validFrom === value.validFrom
        )?.validFrom;

        if (existingItem) {
          const defaultValueItem = parseDate(parseDate(existingItem).toDateString()).getTime();

          // Allow old definitions (edit) to pass through this validation
          if (defaultValueItem === validFromDate) {
            return;
          }
        }

        // Check if field value is from the past
        if (validFromDate < nowDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('entity.accounting.validations.noPast'),
            path: [index, 'validFrom'],
          });
        }
      });
    });

export const documentRoundingDefinitionFormValidations = (
  edit?: boolean,
  defaultValue?: DocumentRoundingDefinitionDefaultValue
) =>
  z.object({
    branchId: edit ? z.string().nullable() : z.string(),
    paymentType: z.enum([...PaymentTypeSchema.options, 'offset']),
    validities: documentRoundingDefinitionValidityValidations(defaultValue),
    isSystem: z.boolean().optional(),
  });
